import React, { memo, ReactNode } from "react";
import { Platform, StyleProp, ViewStyle } from "~/ui/primitives";
import styled, { useTheme } from "~/ui/styled";
import { Flex } from "~components/El";

interface Props {
  label?: string | ReactNode;
  value: string;
  prefix?: string | ReactNode;
  placeholder?: string;
  maxLength?: number;
  editable?: boolean;
  required?: boolean;
  keyboardType?: string;
  decimal?: number;
  secureTextEntry?: boolean;
  suffix?: string | ReactNode;
  error?: string | boolean;
  children?: ReactNode;
  style?: StyleProp<ViewStyle>;
  onChangeText?: (t: string) => void;
  onBlur?: () => void;
}

const TextField: React.FC<Props> = ({
  label = "",
  value = "",
  placeholder = "请输入",
  maxLength = 20,
  editable = true,
  required = false,
  keyboardType = "default",
  decimal = 2,
  secureTextEntry = false,
  prefix = "",
  suffix = "",
  error = "",
  style = {},
  children = null,
  onChangeText = () => {},
  onBlur = () => {},
}) => {
  if (children) return children;
  const theme = useTheme();

  const handleChange = (v: string) => {
    let filtered = v;

    switch (keyboardType) {
      case "numeric":
        filtered = v.replace(/[^0-9]/g, "");
        break;

      case "number-pad":
        filtered = v.replace(/[^0-9-]/g, "");
        filtered = filtered.replace(/(?!^)-/g, ""); // 只能第一个位置有负号
        break;

      case "decimal-pad":
        filtered = v.replace(/[^0-9.]/g, "");
        const parts = filtered.split(".");
        if (parts.length > 2) filtered = parts[0] + "." + parts[1];
        if (parts[1]?.length > decimal) filtered = parts[0] + "." + parts[1].slice(0, decimal);
        break;

      default:
        break;
    }

    onChangeText(filtered);
  };

  return (
    <FormGroup>
      {label && (
        <LabelWrapper>
          {typeof label === "string" ? <LabelText>{required && <RequiredText>*&nbsp;</RequiredText>}{label}</LabelText> : label}
        </LabelWrapper>
      )}

      <FieldContent error={!!error} style={style}>
        {prefix && (
          <Prefix>
            {typeof prefix === "string" ? <PrefixText>{prefix}</PrefixText> : prefix}
          </Prefix>
        )}

        <Input
          value={value}
          editable={editable}
          placeholder={placeholder}
          keyboardType={keyboardType}
          secureTextEntry={secureTextEntry}
          maxLength={maxLength}
          placeholderTextColor={theme.colors.inputPlaceholderColor}
          underlineColorAndroid="transparent"
          onChangeText={handleChange}
          onBlur={onBlur}
        />

        {suffix && (
          <Suffix>
            {typeof suffix === "string" ? <SuffixText>{suffix}</SuffixText> : suffix}
          </Suffix>
        )}
      </FieldContent>

      {error ? (
        <ErrorText>{error}</ErrorText>
      ) : null}
    </FormGroup>
  );
};

// ---------------- Styled ------------------

const FormGroup = styled.View`
  /* flex: 1; */
  flex-direction: column;
  width: 100%;
`;

const LabelWrapper = styled.View`
  flex-direction: row;
  align-items: center;
  padding: 12px 0 8px 0;
`;

const LabelText = styled.Text`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 600;
`;

const RequiredText = styled.Text`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.error};
  font-weight: 400;
`;

const FieldContent = styled(Flex)<{ error: boolean }>`
  flex-direction: row;
  align-items: center;
  height: 48px;
  border-radius: 8px;
  padding: 0 6px;
  overflow: hidden;
  ${({ theme, error }) =>
    Platform.OS === "web"
      ? `
    border: 1.5px solid ${error ? theme.colors.error : theme.colors.border};
    background-color: ${theme.colors.blockBackground};
  `
      : `
    border-width: 1px;
    border-color: ${error ? theme.colors.error : theme.colors.border};
  `}
`;

const Input = styled.TextInput`
  flex: 1;
  height: 100%;
  padding: 0 10px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  ${Platform.OS === "web" ? "outline: none; border: none; background: transparent;" : ""}
`;

const Prefix = styled.View`
  flex-direction: row;
  align-items: center;
`;

const PrefixText = styled.Text`
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
`;

const Suffix = styled.View`
  flex-direction: row;
  align-items: center;
`;

const SuffixText = styled.Text`
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 14px;
`;

const ErrorText = styled.Text`
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.error};
`;

export default memo(TextField);
